// Utility functions for getting document details in Microsoft Word using Office JavaScript API

import { debugError, debugWarn } from "./debug";
import { WORD_RUN_TIMEOUT_MS, withTimeout } from "./timeout";

export interface DocumentInfo {
  name: string;
  url?: string;
  size: number; // in bytes
  id?: string;
}

// Cache to prevent multiple ID generation in the same session
let cachedDocumentId: string | null = null;
let documentIdPromise: Promise<string> | null = null;

export function resetDocumentIdCacheForTests(): void {
  cachedDocumentId = null;
  documentIdPromise = null;
}

/**
 * Generate a unique GUID for document identification
 */
const generateGUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DOCUMENT_ID_PROPERTY = "aila_document_id";

function readCustomDocumentId(docIdProp: { isNullObject: boolean; value: unknown }): string | null {
  if (docIdProp.isNullObject) return null;
  if (typeof docIdProp.value !== "string" || docIdProp.value === "") return null;
  return docIdProp.value;
}

async function writeCustomDocumentId(
  context: Word.RequestContext,
  customProperties: { add: (name: string, value: string) => void },
  documentId: string
): Promise<void> {
  customProperties.add(DOCUMENT_ID_PROPERTY, documentId);
  // Do NOT call context.document.save(). On Mac, save() on an unsaved
  // sideload/temp document can open a hidden Save dialog and hang forever.
  await context.sync();
}

/**
 * Get or create a unique document ID stored in custom properties.
 * A timed-out getDocumentInfo may already have published a session id —
 * keep using that id, and still persist it when the Word file has none.
 */
const getOrCreateDocumentId = async (context: Word.RequestContext): Promise<string> => {
  if (documentIdPromise) {
    return documentIdPromise;
  }

  documentIdPromise = (async () => {
    try {
      const customProperties = context.document.properties.customProperties;
      const docIdProp = customProperties.getItemOrNullObject(DOCUMENT_ID_PROPERTY);
      docIdProp.load(["value"]);
      await context.sync();

      const existingId = readCustomDocumentId(docIdProp);
      if (existingId != null) {
        cachedDocumentId = cachedDocumentId ?? existingId;
        return cachedDocumentId;
      }

      const documentId = cachedDocumentId ?? generateGUID();
      cachedDocumentId ??= documentId;
      await writeCustomDocumentId(context, customProperties, cachedDocumentId);
      return cachedDocumentId;
    } catch (error) {
      console.error("Error getting or creating document ID:", error);
      throw error;
    } finally {
      documentIdPromise = null;
    }
  })();

  return documentIdPromise;
};

function fallbackDocumentInfo(reason: string): DocumentInfo {
  const id = cachedDocumentId ?? generateGUID();
  cachedDocumentId = id;
  debugWarn("document", "using session document id; Word API unavailable", { reason, id });
  return {
    name: "Untitled Document",
    size: 0,
    id,
  };
}

/**
 * This Word file's aila_document_id already belongs to another AILA account
 * (GET /documents/{id} 403). Mint a new GUID, persist it, and let the 404
 * create path register a document for the current user.
 */
export async function rebindWordDocumentId(): Promise<string> {
  documentIdPromise = null;
  const documentId = generateGUID();
  cachedDocumentId = documentId;
  try {
    await withTimeout(
      Word.run(async (context) => {
        const customProperties = context.document.properties.customProperties;
        await writeCustomDocumentId(context, customProperties, documentId);
      }),
      WORD_RUN_TIMEOUT_MS,
      "Word.run timed out in rebindWordDocumentId"
    );
  } catch (error) {
    debugWarn("document", "could not persist rebound Word document id", {
      message: error instanceof Error ? error.message : String(error),
    });
  }
  return documentId;
}

/**
 * Get document information including name, URL, and size.
 * Must always resolve: a hanging Word.run used to leave msWordId null, which
 * disabled every feature query so licensed users saw a live pane with no APIs.
 */
export const getDocumentInfo = async (): Promise<DocumentInfo> => {
  try {
    return await withTimeout(
      Word.run(async (context) => {
        const body = context.document.body;
        body.load(["text"]);
        const documentId = await getOrCreateDocumentId(context);
        const documentText = body.text;
        const sizeInBytes = new Blob([documentText]).size;
        const documentUrl = Office.context.document.url;
        let documentName = "Untitled Document";
        if (documentUrl != null && documentUrl !== "") {
          const urlParts = documentUrl.split("/");
          const filename = urlParts[urlParts.length - 1];
          if (filename != null && filename !== "") {
            documentName = decodeURIComponent(filename);
          }
        }
        return {
          name: documentName,
          url: documentUrl,
          size: sizeInBytes,
          id: documentId,
        };
      }),
      WORD_RUN_TIMEOUT_MS,
      "Word.run timed out in getDocumentInfo"
    );
  } catch (error) {
    debugError("document", "Word.run failed in getDocumentInfo", {
      message: error instanceof Error ? error.message : String(error),
    });
    return fallbackDocumentInfo(error instanceof Error ? error.message : String(error));
  }
};

/**
 * Set up event listener for document content changes
 * Uses both onParagraphChanged and onParagraphInserted to catch all content changes including paste
 */
export const setupDocumentChangeListener = async (
  callback?: () => void
): Promise<() => Promise<void>> => {
  try {
    // Check if the API version is supported
    if (!Office.context.requirements.isSetSupported("WordApi", "1.6")) {
      return async () => {};
    }

    let changedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphChangedEventArgs> | null =
      null;
    let insertedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphAddedEventArgs> | null =
      null;
    let deletedHandler: OfficeExtension.EventHandlerResult<Word.ParagraphDeletedEventArgs> | null =
      null;
    let trackedDocument: Word.Document | null = null;

    await Word.run(async (context) => {
      // Keep this document so cleanup can remove handlers on the same context.
      trackedDocument = context.document;

      // Listen for paragraph changes (edits to existing content)
      changedHandler = context.document.onParagraphChanged.add(async () => {
        if (callback) {
          callback();
        }
      });

      // Listen for paragraph additions (paste, new paragraphs)
      insertedHandler = context.document.onParagraphAdded.add(async () => {
        if (callback) {
          callback();
        }
      });

      // Listen for paragraph deletions (cut, delete)
      deletedHandler = context.document.onParagraphDeleted.add(async () => {
        if (callback) {
          callback();
        }
      });

      await context.sync();
    });

    // Return cleanup function. Microsoft requires the same RequestContext that
    // added the handler to remove it — match useFuzzySearch (Word.run(trackedDocument, ...)).
    return async () => {
      if (trackedDocument == null) {
        return;
      }
      const documentToUnhook = trackedDocument;
      await Word.run(documentToUnhook, async (context) => {
        if (changedHandler) {
          changedHandler.remove();
        }
        if (insertedHandler) {
          insertedHandler.remove();
        }
        if (deletedHandler) {
          deletedHandler.remove();
        }
        await context.sync();
      }).catch(console.error);
    };
  } catch (error) {
    console.error("Error setting up document listener:", error);
    return async () => {};
  }
};
